var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FileLoader } from "three";
import { cacheService } from "../cache/CacheService";
// Copied from https://github.com/mrdoob/three.js/blob/dev/src/loaders/FileLoader.js
const loading = {};
class HttpError extends Error {
    constructor(message, response) {
        super(message);
        this.response = response;
    }
}
class ThreeJsCachingService {
    constructor() {
        this.savedLoadFunc = FileLoader.prototype.load;
    }
    set enabled(value) {
        if (value) {
            // Override load method
            FileLoader.prototype.load = this.fileLoaderLoad;
        }
        else {
            FileLoader.prototype.load = this.savedLoadFunc;
        }
    }
    fileLoaderLoad(url, onLoad, onProgress, onError) {
        const self = this;
        if (url === undefined)
            url = "";
        if (self.path !== undefined)
            url = self.path + url;
        url = self.manager.resolveURL(url);
        // Check if request is duplicate
        if (loading[url] !== undefined) {
            loading[url].push({
                onLoad: onLoad,
                onProgress: onProgress,
                onError: onError
            });
            return;
        }
        // Initialise array for duplicate requests
        loading[url] = [];
        loading[url].push({
            onLoad: onLoad,
            onProgress: onProgress,
            onError: onError
        });
        const promise = cacheService.getWithEtag(url);
        promise.then((cached) => {
            if (cached != undefined && cached.fromMemory) {
                self.manager.itemStart(url);
                setTimeout(() => {
                    const callbacks = loading[url];
                    delete loading[url];
                    for (let i = 0, il = callbacks.length; i < il; i++) {
                        const callback = callbacks[i];
                        if (callback.onLoad)
                            callback.onLoad(cached.data);
                    }
                    self.manager.itemEnd(url);
                }, 0);
                return cached.data;
            }
            const headers = new Headers(self.requestHeader);
            headers.delete("If-None-Match");
            headers.delete("If-Modified-Since");
            if (cached && cached.etag) {
                headers.set("If-None-Match", cached.etag);
            }
            // create request
            const req = new Request(url, {
                headers,
                credentials: self.withCredentials ? "include" : "same-origin"
                // An abort controller could be added within a future PR
            });
            // record states ( avoid data race )
            const mimeType = self.mimeType;
            const responseType = self.responseType;
            let responseEtag;
            let usedCached = false;
            // start the fetch
            fetch(req)
                .then((response) => __awaiter(this, void 0, void 0, function* () {
                if (response.status !== 304 || cached !== undefined) {
                    return response;
                }
                console.warn(`[web] Received HTTP 304 without cached data for "${url}"; retrying without cache validators`);
                const retryHeaders = new Headers(headers);
                retryHeaders.delete("If-None-Match");
                retryHeaders.delete("If-Modified-Since");
                return yield fetch(new Request(req, {
                    headers: retryHeaders,
                    cache: "reload"
                }));
            }))
                .then((response) => {
                if (response.status === 500) {
                    console.warn("[web] Got HTTP 500 when fetching assets from minio: ", url, response);
                }
                if (response.status === 304 && cached !== undefined) {
                    // the resource has not changed
                    usedCached = true;
                    cacheService.usedCache(url);
                    return cached.data;
                }
                if (response.status === 200 || response.status === 0) {
                    if (!response.body) {
                        return;
                    }
                    // Some browsers return HTTP Status 0 when using non-http protocol
                    // e.g. 'file://' or 'data://'. Handle as success.
                    if (response.status === 0) {
                        console.warn("[web] THREE.FileLoader: HTTP Status 0 received.");
                    }
                    responseEtag = response.headers.get("etag");
                    // Workaround: Checking if response.body === undefined for Alipay browser #23548
                    if (typeof ReadableStream === "undefined" || response.body === undefined || response.body.getReader === undefined) {
                        return response;
                    }
                    const callbacks = loading[url];
                    const reader = response.body.getReader();
                    const contentLength = response.headers.get("Content-Length");
                    const total = contentLength ? parseInt(contentLength) : 0;
                    const lengthComputable = total !== 0;
                    let loaded = 0;
                    // periodically read data into the new stream tracking while download progress
                    const stream = new ReadableStream({
                        start(controller) {
                            readData();
                            function readData() {
                                reader.read().then(({ done, value }) => {
                                    if (done) {
                                        controller.close();
                                    }
                                    else {
                                        loaded += value.byteLength;
                                        const event = new ProgressEvent("progress", { lengthComputable, loaded, total });
                                        for (let i = 0, il = callbacks.length; i < il; i++) {
                                            const callback = callbacks[i];
                                            if (callback.onProgress)
                                                callback.onProgress(event);
                                        }
                                        controller.enqueue(value);
                                        readData();
                                    }
                                });
                            }
                        }
                    });
                    return new Response(stream);
                }
                else {
                    throw new HttpError(`fetch for "${response.url}" responded with ${response.status}: ${response.statusText}`, response);
                }
            })
                .then((response) => {
                if (!response) {
                    return;
                }
                if (usedCached) {
                    return response;
                }
                switch (responseType) {
                    case "arraybuffer":
                        return response.arrayBuffer();
                    case "blob":
                        return response.blob();
                    case "document":
                        return response.text().then((text) => {
                            const parser = new DOMParser();
                            return parser.parseFromString(text, mimeType);
                        });
                    case "json":
                        return response.json();
                    default:
                        if (mimeType === undefined) {
                            return response.text();
                        }
                        else {
                            // sniff encoding
                            const re = /charset="?([^;"\s]*)"?/i;
                            const exec = re.exec(mimeType);
                            const label = exec && exec[1] ? exec[1].toLowerCase() : undefined;
                            const decoder = new TextDecoder(label);
                            return response.arrayBuffer().then((ab) => decoder.decode(ab));
                        }
                }
            })
                .then((data) => {
                // Add to cache only on HTTP success, so that we do not cache
                // error response bodies as proper responses to requests.
                // Cache.add(url, data);
                if (data && !usedCached) {
                    cacheService.add(url, data, responseEtag);
                }
                const callbacks = loading[url];
                delete loading[url];
                for (let i = 0, il = callbacks.length; i < il; i++) {
                    const callback = callbacks[i];
                    if (callback.onLoad)
                        callback.onLoad(data);
                }
            })
                .catch((err) => {
                // Abort errors and other errors are handled the same
                const callbacks = loading[url];
                if (callbacks === undefined) {
                    // When onLoad was called and url was deleted in `loading`
                    self.manager.itemError(url);
                    throw err;
                }
                delete loading[url];
                for (let i = 0, il = callbacks.length; i < il; i++) {
                    const callback = callbacks[i];
                    if (callback.onError)
                        callback.onError(err);
                }
                self.manager.itemError(url);
            })
                .finally(() => {
                self.manager.itemEnd(url);
            });
            self.manager.itemStart(url);
        });
    }
}
export const threeJsCachingService = new ThreeJsCachingService();
